  // PdfViewer.jsx
  import React, { useState, useEffect, useMemo, useRef } from "react";
  import { Document, Page, pdfjs } from "react-pdf";
  import PropTypes from "prop-types";
  import "react-pdf/dist/esm/Page/TextLayer.css";
  import "react-pdf/dist/esm/Page/AnnotationLayer.css";
  import Papa from "papaparse";
  import { ChevronLeft, ChevronRight, ZoomOut, ZoomIn } from "lucide-react";

  pdfjs.GlobalWorkerOptions.workerSrc =
    "//cdnjs.cloudflare.com/ajax/libs/pdf.js/" +
    pdfjs.version +
    "/pdf.worker.min.js";


    
  /* ---------------- helpers ---------------- */
  const getExt = (url = "") =>
    url.split("?")[0].split("#")[0].split(".").pop().toLowerCase();

  /* ------------ top bar component ------------ */
  const btnStyle = {
    border: "none",
    background: "transparent",
    padding: 4,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  };



  const TopToolbar = ({
    pageNumber,
    totalPages,
    zoom,
    fileName,
    prevPage,
    nextPage,
    zoomOut,
    zoomIn,
  }) => (
    <div
      style={{
        height: 44,
        display: "flex",
        alignItems: "center",
        gap: 12,
        padding: "0 16px",
        fontFamily: "Inter, sans-serif",
      }}
    >
      {/* page pill */}
      <span
        style={{
          background: "#f5f5f5",
          borderRadius: 6,
          padding: "3px 10px",
          fontSize: 12,
          fontWeight: 600,
          minWidth: 32,
          textAlign: "center",
        }}
      >
        
        {pageNumber}
      </span>
      <span style={{ fontSize: 14, color: "#6b6b6b" }}>
        of {totalPages || "?"}
      </span>

      
      {/* <button onClick={prevPage} disabled={pageNumber === 1} style={btnStyle}>
      <ChevronLeft size={20} color="#c44dc0" strokeWidth={2.2} />
      </button>
      <button
        onClick={nextPage}
        disabled={totalPages && pageNumber === totalPages}
        style={btnStyle}
      >
        <ChevronRight size={20} color="#c44dc0" strokeWidth={2.2} />
      </button> */}

      {/* file name */}
      <span
        style={{
          flex: 1,
          textAlign: "center",
          fontWeight: 600,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
        title={fileName}         
      >
        {/* {fileName} */}
      </span>
      {/* zoom */}
      {/* <button onClick={zoomOut} style={btnStyle}>
        <ZoomOut size={18} />
      </button>
      <span style={{ minWidth: 48, textAlign: "center" }}>
        {Math.round(zoom * 100)}%
      </span>
      <button onClick={zoomIn} style={btnStyle}>
        <ZoomIn size={18} />
      </button> */}
    </div>
  );

  /* ---------------- main component ---------------- */
const PdfViewer = ({
  pdfUrl,
  highlightPage = 1,
  highlightText = "",
  highlightBoxes = [],
  resetScrollKey, // ✅ NEW PROP
}) => {

    /* ---------- pdf-specific state ---------- */
       const [pageDims, setPageDims] = useState({}); // key: pageIndex, value: { width, height }

    const [pageNumber, setPageNumber] = useState(highlightPage);
    const [totalPages, setTotalPages] = useState(null);
    const [loadError, setLoadError] = useState(null);
const [zoom, setZoom] = useState(0.92); // Initial zoom is fixed
    const [containerWidth, setContainerWidth] = useState(600);
    const [csvRows, setCsvRows] = useState([]);
  const pageRefs = useRef([]);
  const scrollableRef = useRef(null);
const [scrollTriggered, setScrollTriggered] = useState(false);

    /* ---------- shared ---------- */
    const containerRef = useRef(null);
    const ext = useMemo(() => getExt(pdfUrl), [pdfUrl]);
    const fileName = useMemo(
      () => pdfUrl.split("/").pop().split("?")[0],
      [pdfUrl]
    );
useEffect(() => {
    if (!pdfUrl) return;

    const loadPDF = async () => {
      try {
        const loadingTask = pdfjs.getDocument(pdfUrl);
        const pdf = await loadingTask.promise;

        // render first page or full document here
      } catch (err) {
        console.error("❌ Failed to load PDF:", err);
      }
    };

    loadPDF();
  }, [pdfUrl]); // 👈 rerun when pdfUrl changes
    const memoizedFile = useMemo(
      () => ({ url: pdfUrl, withCredentials: false }),
      [pdfUrl]
    );


const hasSetInitialPage = useRef(false);

useEffect(() => {
  if (hasSetInitialPage.current) return;

  if (highlightPage && typeof highlightPage === "number") {
    setPageNumber(highlightPage);
    hasSetInitialPage.current = true;
  } else if (Array.isArray(highlightText) && highlightText.length > 0) {
    const firstPage = highlightText[0].page_number;
    if (typeof firstPage === "number") {
      setPageNumber(firstPage + 1); // Pages are 1-indexed
      hasSetInitialPage.current = true;
    }
  }
}, [highlightPage, highlightText]);



useEffect(() => {
  if (!highlightBoxes || highlightBoxes.length === 0 || scrollTriggered) return;

  const firstPage = highlightBoxes.reduce(
    (min, box) => Math.min(min, box.page),
    Infinity
  );

  const scrollAfterRender = () => {
    const pageEl = pageRefs.current[firstPage];
    if (pageEl) {
      pageEl.scrollIntoView({ behavior: "smooth", block: "start" });
      setScrollTriggered(true);
    } else {
      setTimeout(scrollAfterRender, 100);
    }
  };

  scrollAfterRender();
}, [highlightBoxes, scrollTriggered]);


useEffect(() => {
  if (!highlightText || highlightText.length === 0) return;

  const page = highlightText[0]?.page_number;
  if (typeof page === "number") {
    const pageEl = pageRefs.current[page];
    if (pageEl) {
      pageEl.scrollIntoView({ behavior: "smooth", block: "start" });
    } else {
      // Retry if page is not rendered yet
      const retryScroll = setTimeout(() => {
        const el = pageRefs.current[page];
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 300);
      return () => clearTimeout(retryScroll);
    }
  }
}, [highlightText, pdfUrl]);

// Scroll to highlightPage once every page above it has rendered, so they can't push it off target.
const scrolledToPage = useRef(false);
useEffect(() => {
  if (scrolledToPage.current || !(highlightPage > 1) || !totalPages) return;
  const target = Math.min(highlightPage, totalPages) - 1;
  for (let i = 0; i <= target; i++) if (!pageDims[i]) return;
  pageRefs.current[target]?.scrollIntoView({ block: "start" });
  scrolledToPage.current = true;
}, [pageDims, totalPages, highlightPage]);







useEffect(() => {
  setZoom(0.92);  // Lock zoom level
}, []);



  useEffect(() => {
    const scrollContainer = scrollableRef.current;
    if (!scrollContainer) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const visiblePages = entries
          .filter((entry) => entry.isIntersecting)
          .map((entry) => parseInt(entry.target.getAttribute("data-page")));

        if (visiblePages.length > 0) {
          const topPage = Math.min(...visiblePages);
          setPageNumber(topPage);
        }
      },
      {
        root: scrollContainer,
        threshold: 0.5,
      }
    );

    pageRefs.current.forEach((pageEl, i) => {
      if (pageEl) {
        pageEl.setAttribute("data-page", i + 1);
        observer.observe(pageEl);
      }
    });

    return () => observer.disconnect();
  }, [totalPages]);


    /* -------- CSV parsing -------- */
    useEffect(() => {
      if (ext === "csv") {
        fetch(pdfUrl)
          .then((res) => res.text())
          .then((txt) => {
            const { data } = Papa.parse(txt.trim(), { skipEmptyLines: true });
            setCsvRows(data);
          })
          .catch(() => setCsvRows([["❌ Failed to load CSV"]]));
      }
    }, [pdfUrl, ext]);






    

    /* update width on resize */
    useEffect(() => {
      const updateWidth = () =>
        containerRef.current &&
        setContainerWidth(containerRef.current.offsetWidth);
      updateWidth();
      window.addEventListener("resize", updateWidth);
      return () => window.removeEventListener("resize", updateWidth);
    }, []);

    /* ============ NON-PDF RENDERERS ============ */

    const csvViewer = (
      <div style={{ height: "89vh", overflow: "auto" }}>
        <table style={{ borderCollapse: "collapse", width: "100%" }}>
          <tbody>
            {csvRows.map((row, r) => (
              <tr key={r}>
                {row.map((cell, c) => (
                  <td
                    key={c}
                    style={{
                      border: "1px solid #ccc",
                      padding: "4px 8px",
                      fontFamily: "monospace",
                    }}
                  >
                    {cell}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );

    const officeIframe = (
      <iframe
        title="office-viewer"
        src={`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(
          pdfUrl
        )}`}
        style={{ border: 0, width: "100%", height: "89vh" }}
      />
    );

    const [textContent, setTextContent] = useState("");
    useEffect(() => {
      if (["txt", "csv"].includes(ext)) {
        fetch(pdfUrl)
          .then((res) => res.text())
          .then(setTextContent)
          .catch(() => setTextContent("❌ Failed to load file."));
      }
    }, [pdfUrl, ext]);

    const textViewer = (
      <pre
        style={{
          height: "89vh",
          overflow: "auto",
          padding: 16,
          background: "#f9f9f9",
          border: "1px solid #ccc",
          borderRadius: 8,
          fontFamily: "Consolas, monospace",
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
        }}
      >
        {textContent || "Loading..."}
      </pre>
    );

    /* ============ MAIN RENDER SWITCH ============ */
    if (ext !== "pdf") {
      if (["doc", "docx", "xls", "xlsx", "ppt", "pptx"].includes(ext))
        return officeIframe;
      if (ext === "csv") return csvViewer;
      if (ext === "txt") return textViewer;
  if (["jpg", "jpeg", "png", "webp", "bmp"].includes(ext)) {
    return (
      <div
        style={{
          maxHeight: "89vh",
          overflow: "auto",           // allow scroll if image overflows
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          backgroundColor: "#fff",
          border: "1px solid #ccc",
          borderRadius: "8px",
        }}
      >
        <img
          src={pdfUrl}
          alt="Uploaded"
          style={{
            display: "block",
            maxWidth: "100%",
            maxHeight: "none",       // don't scale down the image height
            height: "auto",
            width: "auto",
          }}
        />
      </div>
    );
  }


      return (
        <div
          style={{
            height: "89vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            border: "1px solid #ccc",
            borderRadius: 8,
          }}
        >
          Unsupported file type.
        </div>
      );
    }

    /* ============ ORIGINAL PDF VIEW ============ */


const handleLoadSuccess = ({ numPages }) => {
  setTotalPages(numPages);
  setLoadError(null);
};

    const handleLoadError = (error) => {
      console.error("❌ Failed to load PDF:", error);
      setLoadError("Failed to load PDF. Please check the URL or CORS settings.");
    };


    
    return (
      <>
          <style>{`
        .textLayer span.highlighted {
          background: #D1C4F2;        /* yellow box */
          border-radius: 2px;
          color: #000 !important;     /* make glyphs visible */
          -webkit-text-fill-color: #000 !important;
        }
      `}</style>
    
        {/* ───────── viewer container ───────── */}
        <div
          ref={containerRef}
          style={{
            position: "relative",
            width: "100%",
            height: "100%",               // full height; toolbar sits *inside*
            background: "#fff ",
            border: "1px solid var(--Stroke-black, rgba(0, 0, 0, 0.10))",
            boxShadow:"0px 100px 28px 0px rgba(0, 0, 0, 0.00), 0px 64px 26px 0px rgba(0, 0, 0, 0.01), 0px 36px 22px 0px rgba(0, 0, 0, 0.05), 0px 16px 16px 0px rgba(0, 0, 0, 0.09), 0px 4px 9px 0px rgba(0, 0, 0, 0.10)",
            borderRadius: 8,
            display: "flex",
            flexDirection: "column",
          }}
        >
          {/* top toolbar (now inside container, always above the page) */}
          <TopToolbar
            pageNumber={pageNumber}
            totalPages={totalPages}
            zoom={zoom}
            fileName={fileName}
            prevPage={() => setPageNumber((p) => Math.max(p - 1, 1))}
            nextPage={() =>
              setPageNumber((p) =>
                totalPages ? Math.min(p + 1, totalPages) : p
              )
            }
            zoomOut={() => setZoom((z) => Math.max(z - 0.1, 0.5))}
            zoomIn={() => setZoom((z) => Math.min(z + 0.1, 2))}
          />
    
          {/* scrollable PDF section */}
         <div
  ref={scrollableRef}
  style={{
    flex: 1,
    overflowY: "auto",
    overflowX: "hidden",
    padding: "0 20px",
    position: "relative",
  }}
>

          <Document
    file={memoizedFile}
    onLoadSuccess={handleLoadSuccess}
    onLoadError={handleLoadError}
    loading={<p>Loading PDF...</p>}
    error={<p>{loadError}</p>}
  >
{[...Array(totalPages).keys()].map((_, index) => {

  const pageBoxes = (highlightBoxes || []).filter((box, i) => {
    if (!box) {
      console.warn(`⚠️ highlightBoxes[${i}] is undefined or null`, box);
      return false;
    }

    if (typeof box.page !== "number") {
      console.warn(`⚠️ highlightBoxes[${i}].page is not a number:`, box);
      return false;
    }

    return box.page === index;
  });
  return (
    <div
      key={index}
      ref={(el) => (pageRefs.current[index] = el)}
      data-page={index + 1}
      style={{
        position: "relative",
        display: "flex",
        justifyContent: "center",
        marginBottom: 16,
      }}
    >
      <Page
        pageNumber={index + 1}
        width={containerWidth * zoom}
        renderAnnotationLayer={false}
        renderTextLayer={true}
  onRenderSuccess={async (page) => {
  const pageIndex = index;
  const viewport = await page.getViewport({ scale: 1 });

  setPageDims((prev) => ({
    ...prev,
    [pageIndex]: { width: viewport.width, height: viewport.height },
  }));

  const renderedPageWidth = containerWidth * zoom;
  const zoomRatio = renderedPageWidth / viewport.width;

  const pageEl = pageRefs.current[pageIndex];
  if (!pageEl) return;

  // Clear previous highlights
  pageEl.querySelectorAll(".highlight-annotation").forEach((el) => el.remove());

  // ✅ New: Highlight full page if in highlightText
  const highlightPages = (highlightText || []).map((h) => h.page_number);
if (highlightPages.includes(index)) {
  const highlight = document.createElement("div");
  highlight.className = "highlight-annotation";
  Object.assign(highlight.style, {
    top: 0,
    left: 0,
    width: `${viewport.width * zoomRatio}px`,
    height: `${viewport.height * zoomRatio}px`,
    position: "absolute",
    backgroundColor: "rgba(0, 0, 255, 0.1)",
    zIndex: 5,
    pointerEvents: "none",
  });
  pageEl.appendChild(highlight);
}

}}




      />
    </div>
  );
})}



  </Document>

          </div>
        </div>
    
     

      </>
    );
    
  };


  PdfViewer.propTypes = {
    pdfUrl: PropTypes.string.isRequired,
    highlightPage: PropTypes.number,
    highlightText: PropTypes.arrayOf(
    PropTypes.shape({
      page_number: PropTypes.number.isRequired,
      page_content: PropTypes.string.isRequired,
    })
  )
  //
  };

  export default PdfViewer;